import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AgenticEmptyState from 'components/@home/@settings/Tabs/Agents/AgenticEmptyState';
import AttachSkillsDialog from 'components/@home/@settings/Tabs/Agents/AttachSkillsDialog';
import AgenticPanelToolbar from 'components/@home/@settings/Tabs/Agents/panels/AgenticPanelToolbar';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import FileTreeOutlineIcon from 'mdi-react/FileTreeOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import TrayArrowUpIcon from 'mdi-react/TrayArrowUpIcon';
import { arrayOf, func, object } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { I18n } from 'utils/i18n';

const nameCellSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  height: '100%',
  minWidth: 0,
  pt: 1,
  pb: 1,
};

const nameRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  fontWeight: 600,
  color: 'text.primary',
  lineHeight: 1.3,
};

const descriptionSx = {
  fontSize: theme => theme.typography.pxToRem(12),
  color: 'text.secondary',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pl: 3,
};

const SkillsPanel = ({
  agent,
  skills,
  onSaveAgent,
  onCreateSkill = null,
  onImportSkill = null,
}) => {
  const navigate = useNavigate();
  const { flow, authCompany } = useAgentEdit();
  const [manageOpen, setManageOpen] = useState(false);

  const attached = useMemo(() => {
    const attachedIds = (agent?.skillIds || []).map(String);
    return skills.filter(s => attachedIds.includes(String(s._id)));
  }, [skills, agent?.skillIds]);

  const rows = useMemo(
    () =>
      attached.map(skill => ({
        ...skill,
        id: skill._id,
        toolCount: (skill.attachedTools || []).length,
        knowledgeCount: (skill.attachedKnowledge || []).length,
      })),
    [attached],
  );

  const handleOpenSkill = id => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'skills', skillId: id }));
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: I18n.t('AgenticChatFlow.Name'),
        flex: 2.5,
        minWidth: 280,
        valueGetter: (value, row) => row.displayName || row.name,
        renderCell: ({ row }) => (
          <Box sx={nameCellSx}>
            <Box component="span" sx={nameRowSx}>
              <FileTreeOutlineIcon size={16} />
              {row.displayName || row.name}
            </Box>
            {row.description ? (
              <Box sx={descriptionSx} title={row.description}>
                {row.description}
              </Box>
            ) : null}
          </Box>
        ),
      },
      {
        field: 'toolCount',
        headerName: I18n.t('AgenticChatFlow.Tools'),
        type: 'number',
        flex: 0.5,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'knowledgeCount',
        headerName: I18n.t('AgenticChatFlow.Knowledge'),
        type: 'number',
        flex: 0.5,
        minWidth: 120,
        align: 'right',
        headerAlign: 'right',
      },
    ],
    [],
  );

  const toolbarActions = (
    <>
      {onImportSkill && (
        <Button variant="outlined" color="secondary" onClick={onImportSkill}>
          <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
            <TrayArrowUpIcon />
          </SvgIcon>
          {I18n.t('AgenticChatFlow.Import')}
        </Button>
      )}
      <Button variant="outlined" color="secondary" onClick={() => setManageOpen(true)}>
        <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
          <CogOutlineIcon />
        </SvgIcon>
        {I18n.t('AgenticChatFlow.ManageSkills')}
      </Button>
      {onCreateSkill && (
        <Button variant="outlined" color="secondary" onClick={onCreateSkill}>
          <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
            <PlusIcon />
          </SvgIcon>
          {I18n.t('AgenticChatFlow.CreateSkill')}
        </Button>
      )}
    </>
  );

  return (
    <Box
      sx={{
        p: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        flex: 1,
        minHeight: 0,
      }}
    >
      {rows.length === 0 ? (
        <AgenticEmptyState
          concept="skill"
          title={I18n.t('AgenticChatFlow.EmptySkills.Title')}
          lead={I18n.t('AgenticChatFlow.EmptySkills.Lead')}
          secondaryLabel={I18n.t('AgenticChatFlow.ManageSkills')}
          secondaryIcon={<CogOutlineIcon size={16} />}
          onSecondary={() => setManageOpen(true)}
          primaryLabel={onCreateSkill ? I18n.t('AgenticChatFlow.CreateSkill') : null}
          primaryIcon={<PlusIcon size={16} />}
          onPrimary={onCreateSkill || undefined}
        />
      ) : (
        <>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(22),
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {I18n.t('AgenticChatFlow.AllSkills')}
          </Typography>
          <OmmnioDataGrid
            rows={rows}
            columns={columns}
            columnHeaderHeight={48}
            getRowId={r => r._id}
            rowCount={rows.length}
            autoHeight={false}
            disableEvenOdd
            rowHeight={64}
            defaultSortModel={[{ field: 'name', sort: 'asc' }]}
            onCellClick={cell => handleOpenSkill(cell.row._id)}
            slotProps={{
              toolbar: {
                searchPlaceholder: I18n.t('AgenticChatFlow.Search'),
                children: toolbarActions,
              },
            }}
            slots={{ toolbar: AgenticPanelToolbar }}
            sx={{
              flex: 1,
              minHeight: 320,
              borderRadius: 1,
              '& .MuiDataGrid-cell': { cursor: 'pointer' },
            }}
          />
        </>
      )}

      <AttachSkillsDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        skills={skills}
        selectedIds={agent?.skillIds || []}
        onSave={skillIds => onSaveAgent({ skillIds })}
        onCreateSkill={onCreateSkill}
      />
    </Box>
  );
};

SkillsPanel.propTypes = {
  agent: object.isRequired,
  skills: arrayOf(object).isRequired,
  onSaveAgent: func.isRequired,
  onCreateSkill: func,
  onImportSkill: func,
};

export default SkillsPanel;
